'use client'

import type { Operators } from '../constants'
import type FieldController from '../fields/Controller'
import type {
	FieldConfigType,
	FieldKeys,
	FilterRuleEntity,
	Localization,
} from '../types'

import React, { useCallback, useMemo, useRef } from 'react'
import { GripVerticalIcon, XIcon } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import {
	Sortable,
	SortableContent,
	SortableItem,
	SortableItemHandle,
	SortableOverlay,
} from '@/shared/components/ui/sortable'
import { cn } from '@/shared/lib/utils'

import {
	adjustValueType,
	getFieldByValueType,
	getFieldIcon,
	sanitizeFilterRule,
} from '../helpers'
import { localization } from '../localization'
import { objectMap } from '../utils/utils'
import Combobox from './Combobox'

export type QueryBuilderProps = {
	/**
	 * The date format to use for the query builder
	 * @default 'EU'
	 * @example ```
	 * EU - dd/mm/yyyy
	 * US - mm/dd/yyyy
	 * ```
	 * @example ```
	 * EU - 12/01/2026
	 * US - 01/12/2026
	 * ```
	 */
	dateFormat: 'EU' | 'US'

	/** The configuration object for each field that may be rendered in the refinement bar. */
	fieldConfig: FieldConfigType
	/** The current value of each field in the refinement bar. */
	rules: FilterRuleEntity[]
	/** Which fields, if any, may not be removed from the refinement-bar by the user. */
	irremovableCount?: number
	/**  Localization object to override default localization strings */
	locale?: Localization
	/** Callback to update the rules list */
	onRulesChange: (rules: FilterRuleEntity[]) => void
	/** Error schema */
	errorSchema?: string[] | Record<string, any>[]
	/** Children */
	children: React.ReactNode
	/** Enable referencing in the query builder */
	enableReferencing?: boolean
}
export type QueryBuilderContextValue = {
	dateFormat: 'EU' | 'US'

	enableReferencing?: boolean
	/** The configuration object for each field that may be rendered in the refinement bar. */
	fieldConfig: Record<string, FieldController>
	/** The current value of each field in the refinement bar. */
	rules: FilterRuleEntity[]
	/** Which fields, if any, may not be removed from the refinement-bar by the user. */
	irremovableCount?: number
	/**  Localization object to override default localization strings */
	locale: Localization
	/** Callback to update the rules list */
	onRulesChange: (rules: FilterRuleEntity[]) => void

	fieldKeys: FieldKeys
	rulesKeys: FieldKeys
	update: (index: number, data: any) => void
	append: (data: any) => void
	remove: (index: number) => void
	filterOptions: { label: string; value: string }[]
	errorSchema?: string[] | Record<string, any>[]
}

const QueryBuilderContext = React.createContext<
	QueryBuilderContextValue | undefined
>(undefined)

function useQueryBuilder() {
	const context = React.use(QueryBuilderContext)
	if (!context)
		throw new Error('useQueryBuilder must be used within QueryBuilderProvider')

	return context
}
const QueryBuilder = ({
	dateFormat = 'EU',

	enableReferencing = false,
	fieldConfig: initFields,
	locale = localization,
	rules,
	onRulesChange,
	irremovableCount = 0,
	errorSchema,
	children,
}: QueryBuilderProps) => {
	const fieldConfig = useMemo(
		() =>
			objectMap(initFields, (field, key) => {
				const Controller = field.type.controller

				return new Controller({ key, ...field, locale })
			}) as Record<string, FieldController>,
		[initFields]
	)
	const update = useCallback(
		(index: number, data: any) => {
			const newRules = rules.map((filter: FilterRuleEntity, i: number) =>
				sanitizeFilterRule(index === i ? data : filter)
			)
			onRulesChange(newRules)
		},
		[rules, onRulesChange]
	)
	const append = useCallback(
		(data: any) => onRulesChange([...rules, data]),
		[onRulesChange, rules]
	)

	const remove = useCallback(
		(index: number) => {
			const newRules = rules.filter(
				(filter: FilterRuleEntity, i: number) => index !== i
			)

			onRulesChange(newRules)
		},
		[onRulesChange, rules]
	)
	const contextValue = useMemo(() => {
		const fieldKeys: FieldKeys = Object.keys(fieldConfig)
		const rulesKeys: FieldKeys = rules.map(({ name }) => name)
		const mapKeyToOption = (value: string) => {
			const field = fieldConfig[value]
			const label = field.label || value

			return { label, value }
		}
		const filterOptions = fieldKeys.map(mapKeyToOption)

		return {
			dateFormat,
			fieldConfig,
			fieldKeys,
			irremovableCount,
			rules,
			rulesKeys,
			filterOptions,
			onRulesChange,
			update,
			append,
			remove,
			locale,
			errorSchema,
			enableReferencing,
		}
	}, [
		dateFormat,
		fieldConfig,
		rules,
		irremovableCount,
		onRulesChange,
		update,
		append,
		remove,
		locale,
		errorSchema,
	])

	return (
		<QueryBuilderContext value={contextValue}>{children}</QueryBuilderContext>
	)
}
QueryBuilder.displayName = 'QueryBuilder'

const QueryBuilderRulesList = ({
	className,
	defaultJoinOperator = 'and',
}: {
	className?: string
	defaultJoinOperator?: 'and' | 'or'
}) => {
	const { rules, onRulesChange, update, remove } = useQueryBuilder()
	// Will be added in futire
	if (!rules?.length) return null
	const joinOperator = defaultJoinOperator

	return (
		<Sortable
			value={rules}
			onValueChange={onRulesChange}
			getItemValue={(item) =>
				`${item.name}_${item.operation || '$'}_${item.value || '$'}`
			}
		>
			<SortableContent asChild>
				<div
					role='list'
					data-testid='query-builder-list'
					className={cn('flex flex-col gap-2 overflow-y-auto p-1', className)}
				>
					{rules.map((rule, index) => (
						<QueryBuilderFilterItem
							key={index}
							rule={rule}
							ruleIndex={index}
							joinOperator={joinOperator}
							onFilterUpdate={(index, data) => update(index, data)}
							onFilterRemove={() => remove(index)}
						/>
					))}
				</div>
			</SortableContent>
			<SortableOverlay>
				<div className='flex items-center gap-1'>
					<div className='h-8 min-w-[72px] rounded-lg bg-accent' />
					<div className='h-8 w-32 rounded-lg bg-accent' />
					<div className='h-8 w-32 rounded-lg bg-accent' />
					<div className='h-8 min-w-36 flex-1 rounded-lg bg-accent' />
					<div className='size-8 shrink-0 rounded-lg bg-accent' />
					<div className='size-8 shrink-0 rounded-lg bg-accent' />
				</div>
			</SortableOverlay>
		</Sortable>
	)
}
QueryBuilderRulesList.displayName = 'QueryBuilderRulesList'

const QueryBuilderFilterItem = ({
	rule,
	ruleIndex,
	joinOperator,
	onFilterUpdate,
	onFilterRemove,
}: {
	ruleIndex: number
	rule: FilterRuleEntity
	joinOperator: 'and' | 'or'
	onFilterUpdate: (index: number, rule: FilterRuleEntity) => void
	onFilterRemove: (index: number) => void
}) => {
	const nameFieldRef = useRef<HTMLButtonElement>(null)
	const {
		fieldConfig,
		fieldKeys,
		filterOptions,
		locale,
		irremovableCount,
		errorSchema,
		enableReferencing,
		dateFormat,
	} = useQueryBuilder()
	const key = rule.name
	const invalidMessage = errorSchema?.[ruleIndex]
	let isInvalid = Boolean(invalidMessage)
	const joinOperatorView = (
		<div className='min-w-[72px] text-center'>
			{ruleIndex === 0 ? (
				<span className='text-sm text-muted-foreground'>
					{locale.getWhereLabel()}
				</span>
			) : (
				<span className='text-sm text-muted-foreground'>
					{locale.getCombinatorLabel(joinOperator)}
				</span>
			)}
		</div>
	)
	if (!key) {
		return (
			<div className='flex w-full items-center gap-1 rounded-lg'>
				{joinOperatorView}
				<div
					className={cn('grid w-full grid-cols-4 gap-1')}
					data-index={ruleIndex}
					data-testid='query-builder-row'
				>
					<div className='flex items-center'>
						<Combobox
							ref={nameFieldRef}
							locale={locale}
							initialOpen
							name='field'
							className='w-full'
							invalid={isInvalid}
							placeholder={locale.getFieldPlaceholder()}
							onChange={(value) => {
								const initialValue = fieldConfig[value].getInitialValue?.()
								onFilterUpdate(ruleIndex, {
									...rule,
									...(initialValue || { operation: '', value: '' }),
									name: value,
								})
							}}
							getPrefixIcon={(value) =>
								getFieldIcon(fieldConfig[value]?.type || ({} as any))
							}
							onCloseAutoFocus={() => {
								nameFieldRef.current?.focus()
							}}
							options={filterOptions}
						/>
					</div>
				</div>
				<div className='mr-9 ml-auto flex shrink-0 gap-1'>
					<Button
						size='icon'
						variant='ghost'
						data-testid='query-builder-row-remove'
						onClick={() => onFilterRemove(ruleIndex)}
					>
						<XIcon />
					</Button>
				</div>
			</div>
		)
	}

	/**
	 * Get the field model for the current key
	 */
	let fieldModel = fieldConfig[key]

	// Catch invalid configurations
	if (!fieldModel) {
		// eslint-disable-next-line no-console
		console.error(`Couldn't find a matching field config for key "${key}".`)
		const adjustedFieldType = getFieldByValueType(
			adjustValueType(rule.operation as Operators)
		)
		fieldModel = new adjustedFieldType.controller({
			key,
			label: key,
			type: adjustedFieldType,
			valueType: adjustValueType(rule.operation as Operators),
			locale,
		})
		isInvalid = true
	}

	const field = fieldModel
	const FieldView = field.type.view

	/**
	 * Catch missing views:
	 * This should only really happen when developing a new field type
	 */
	if (!FieldView) {
		throw new Error(
			`Couldn't find the View (${field.type.name}) for key "${key}".`
		)
	}

	const isIrremovable = Boolean(
		irremovableCount && irremovableCount >= ruleIndex + 1
	)
	const isDisabled = isIrremovable

	// Values
	const initialValue = field.getInitialValue?.()
	const storedValue = rule || initialValue
	const ruleId = `${key}_${ruleIndex}`

	return (
		<SortableItem
			disabled={isDisabled}
			value={`${rule.name}_${rule.operation || '$'}_${rule.value || '$'}`}
			asChild
		>
			<div
				role='listitem'
				id={ruleId}
				tabIndex={-1}
				className='flex w-full flex-col'
			>
				<div className='flex items-center gap-1'>
					{joinOperatorView}
					<div
						className='grid w-full grid-cols-4 gap-1'
						data-index={ruleIndex}
						data-testid='query-builder-row'
					>
						<div className='flex items-center'>
							<Combobox
								ref={nameFieldRef}
								name='field'
								className='w-full'
								disabled={isDisabled}
								placeholder={locale.getFieldPlaceholder()}
								value={key}
								onChange={(value) => {
									const initialValue = fieldConfig[value].getInitialValue?.()
									onFilterUpdate(ruleIndex, {
										...rule,
										...(initialValue || {}),
										name: value,
									})
								}}
								getPrefixIcon={(value) =>
									getFieldIcon(fieldConfig[value]?.type || ({} as any))
								}
								invalid={isInvalid}
								options={filterOptions}
								locale={locale}
							/>
						</div>
						<FieldView
							dateFormat={dateFormat}
							field={field}
							invalidMessage={invalidMessage}
							disabled={isDisabled}
							onRemove={() => onFilterRemove(ruleIndex)}
							onChange={(rule: {
								operation: Operators
								value: any
								name?: string
							}) => onFilterUpdate(ruleIndex, { ...rule, name: key })}
							fields={fieldKeys.map((key) => fieldConfig[key])}
							storedValue={storedValue}
							locale={locale}
							enableReferencing={enableReferencing}
						/>
					</div>
					<Button
						size='icon'
						variant='ghost'
						disabled={isIrremovable}
						data-testid='query-builder-row-remove'
						onClick={() => onFilterRemove(ruleIndex)}
					>
						<XIcon />
					</Button>
					<SortableItemHandle asChild>
						<Button variant='ghost' size='icon'>
							<GripVerticalIcon />
						</Button>
					</SortableItemHandle>
				</div>
				{isInvalid && typeof invalidMessage == 'string' && (
					<div className='mb-1 ml-[76px] text-sm text-destructive'>
						{invalidMessage}
					</div>
				)}
			</div>
		</SortableItem>
	)
}
QueryBuilderFilterItem.displayName = 'QueryBuilderFilterItem'

const QueryBuilderAddButton = ({
	type = 'button',
	variant = 'ghost',
	...props
}: React.ComponentProps<typeof Button>) => {
	const { append } = useQueryBuilder()

	return (
		<Button
			type={type}
			variant={variant}
			onClick={() => append({ name: '', operation: '', value: '' })}
			{...props}
		/>
	)
}
QueryBuilderAddButton.displayName = 'QueryBuilderAddButton'

const QueryBuilderEmpty = ({
	className,
	...props
}: React.ComponentProps<'div'>) => {
	const { rules } = useQueryBuilder()
	if (rules.length > 0) return null

	return (
		<div
			className={cn('text-sm text-muted-foreground', className)}
			{...props}
		/>
	)
}
QueryBuilderEmpty.displayName = 'QueryBuilderEmpty'

export {
	QueryBuilder,
	QueryBuilderRulesList,
	QueryBuilderFilterItem,
	QueryBuilderAddButton,
	QueryBuilderEmpty,
}
